import { AMPLITUDE_EVENT_TYPES } from 'constants/amplitude';
import { RegistrationFlow } from 'constants/selfRegistration';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  useRegisterCorporation,
  useRegisterCreditAccountCorporation,
} from 'services/reactQueryHooks/corporations';
import { useGetCorporateClientPlaceholder } from 'services/reactQueryHooks/corporateClientPlaceholders';
import useAmplitude from 'utils/hooks/useAmplitude';
import { useFeatureFlag } from 'components/Providers/FeatureFlag';
import { SUPPORTED_COUNTRIES } from 'constants.js';
import { isUkCorporationCreatedAfterWalletGoLive } from 'utils/walletUtil';
import { formatCorprateClientData } from './helper';

const useRegisterCorporationModal = () => {
  const { sendAmplitudeEvent } = useAmplitude();
  const { showFeature } = useFeatureFlag();

  const { isMobileView: isMobile, locale: preferredLanguage } = useSelector(
    state => state?.settings || {},
  );
  const { restrictedCorporateClientProspect, id: userId } = useSelector(state => state?.user || {});

  const {
    mutate: registerCorporation,
    isLoading: isRegisterCorporationLoading,
    isSuccess: isRegisterCorporationSuccess,
  } = useRegisterCorporation();

  const {
    mutate: registerCreditAccountCorporation,
    isLoading: isRegisterCreditAccountCorporationLoading,
    isSuccess: isRegisterCreditAccountCorporationSuccess,
  } = useRegisterCreditAccountCorporation();

  const {
    data: corporateClientPlaceholder,
    isLoading: isGetCorporateClientPlaceholderLoading,
    isSuccess: isGetCorporateClientPlaceholderSuccess,
  } = useGetCorporateClientPlaceholder(userId);

  const handleRegistration = useCallback(
    ({ formData }) => {
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.selfRegistrationCorporateClientRegistrationSave);
      formData = showFeature('selfRegistrationV2')
        ? formatCorprateClientData({
            ...formData,
            userAccountId: userId,
            preferredLanguage: preferredLanguage,
          })
        : { ...formData, userAccountId: userId, preferredLanguage: preferredLanguage };

      registerCorporation(formData);
    },
    [preferredLanguage, registerCorporation, sendAmplitudeEvent, showFeature, userId],
  );

  const handleCreditAccountRegistration = useCallback(() => {
    sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.selfRegistrationCreditAccountClientRegistrationSave);

    registerCreditAccountCorporation({ userAccountId: userId, preferredLanguage: 'en-GB' });
  }, [registerCreditAccountCorporation, sendAmplitudeEvent, userId]);

  const registrationFlow = useMemo(() => {
    if (!showFeature('selfRegistrationV2')) return RegistrationFlow.SELF_V1;
    if (
      corporateClientPlaceholder?.corporateClientName === null ||
      corporateClientPlaceholder?.country === null
    )
      return RegistrationFlow.SELF_V1;
    // A UK org registered after the wallet go-live is net new: no Allstar credit account, so it
    // gives its address and tax number in the same form as the other countries
    if (
      isGetCorporateClientPlaceholderSuccess &&
      corporateClientPlaceholder?.country === 'GB' &&
      !isUkCorporationCreatedAfterWalletGoLive({ country: corporateClientPlaceholder?.country })
    )
      return RegistrationFlow.SELF_V2_UK;
    if (isGetCorporateClientPlaceholderSuccess) return RegistrationFlow.SELF_V2_NON_UK;
  }, [
    corporateClientPlaceholder?.corporateClientName,
    corporateClientPlaceholder?.country,
    isGetCorporateClientPlaceholderSuccess,
    showFeature,
  ]);

  const countryCode = useMemo(() => {
    if (!corporateClientPlaceholder?.country) return null;

    return SUPPORTED_COUNTRIES.find(
      country => country.countryCode === corporateClientPlaceholder?.country,
    ).countryCode;
  }, [corporateClientPlaceholder?.country]);

  return {
    isMobile,
    isRegisterCorporationLoading,
    isRegisterCorporationSuccess,
    isRegisterCreditAccountCorporationLoading,
    isRegisterCreditAccountCorporationSuccess,
    isGetCorporateClientPlaceholderLoading,
    registrationFlow,
    countryCode,
    prospectUser: restrictedCorporateClientProspect,
    onRegister: handleRegistration,
    onCreditAccountCorporationRegister: handleCreditAccountRegistration,
  };
};

export default useRegisterCorporationModal;
